import React, { useState, useEffect } from 'react';
import ModalBase from './ModalBase';
import GenericSelector, { SelectorOption } from './GenericSelector';
import ExplanationField from './ExplanationField';
import { faFont, faTable } from '@fortawesome/free-solid-svg-icons';
import FormField, { FieldOption } from './FormField';

export type SpecType = 'text' | 'table';

// Options for text spec dropdown
const textSpecOptions: FieldOption[] = [
    { value: 'legal_name', label: 'Legal Name' },
    { value: 'ingredients', label: 'Ingredients' },
    { value: 'allergen_statement', label: 'Allergen Statement' },
    { value: 'quantity', label: 'Net Quantity' },
    { value: 'best_before_date', label: 'Best Before Date' },
    { value: 'storage_conditions', label: 'Storage Conditions' },
    { value: 'business_name_address', label: 'Business Name and Address' },
    { value: 'country_of_origin', label: 'Country of Origin' },
    { value: 'instructions_for_use', label: 'Instructions for Use' },
    { value: 'alcoholic_strength', label: 'Alcoholic Strength' },
    { value: 'serving_number', label: 'Serving Number' },
    { value: 'reference_intake', label: 'Reference Intake' },
];

// Options for table spec dropdown
const tableSpecOptions: FieldOption[] = [
    { value: 'nutrition_declaration', label: 'Nutrition Declaration' },
];

const baseSpecTypeOptions: SelectorOption<SpecType>[] = [
    { value: 'text', label: 'Text Spec', icon: faFont },
    { value: 'table', label: 'Table Spec', icon: faTable },
];

interface AddSpecModalProps {
    onClose: () => void;
    onNext: (
        specType: SpecType,
        specNameValue: string,
        specNameLabel: string,
    ) => void;
    initialSpecType?: SpecType | null;
    initialSpecNameValue?: string;
    existingSpecNames?: string[]; // Optional prop for filtering existing specs
}

const AddSpecModal: React.FC<AddSpecModalProps> = ({
    onClose,
    onNext,
    initialSpecType = null,
    initialSpecNameValue = '',
    existingSpecNames = [],
}) => {
    const [selectedSpecType, setSelectedSpecType] = useState<SpecType | null>(
        initialSpecType,
    );
    const [specName, setSpecName] = useState<string>(initialSpecNameValue);
    const [currentSpecTypeOptions, setCurrentSpecTypeOptions] =
        useState<SelectorOption<SpecType>[]>(baseSpecTypeOptions);

    // Use provided existingSpecNames directly
    const allExistingSpecNames = existingSpecNames;

    const availableTextSpecOptions = textSpecOptions.filter(
        (option) => !allExistingSpecNames.includes(option.value),
    );

    const availableTableSpecOptions = tableSpecOptions.filter(
        (option) => !allExistingSpecNames.includes(option.value),
    );

    useEffect(() => {
        const isTableDisabled = availableTableSpecOptions.length === 0;
        setCurrentSpecTypeOptions(
            baseSpecTypeOptions.map((opt) =>
                opt.value === 'table'
                    ? { ...opt, disabled: isTableDisabled }
                    : opt,
            ),
        );

        if (selectedSpecType === 'table' && isTableDisabled) {
            setSelectedSpecType(null);
            setSpecName('');
        }
    }, [availableTableSpecOptions, selectedSpecType]);

    const isTableSpecValueValid = (name: string) =>
        tableSpecOptions.some((opt) => opt.value === name);

    const handleNext = () => {
        if (!selectedSpecType) {
            alert('Please select a spec type.');
            return;
        }

        let specNameLabel = specName; // Default to specName value (for custom text input)

        if (selectedSpecType === 'table') {
            if (!specName || !isTableSpecValueValid(specName)) {
                alert('Please select a valid spec name for the table.');
                return;
            }
            const selectedOption = tableSpecOptions.find(
                (opt) => opt.value === specName,
            );
            if (selectedOption) {
                specNameLabel = selectedOption.label;
            }
        } else if (selectedSpecType === 'text') {
            const selectedOption = textSpecOptions.find(
                (opt) => opt.value === specName,
            );
            if (selectedOption) {
                specNameLabel = selectedOption.label;
            }
            // If it's not in textSpecOptions, it's a custom name, so specNameLabel remains specName (the custom value itself)
        }

        onNext(selectedSpecType, specName, specNameLabel);
    };

    const renderSpecNameField = () => {
        if (!selectedSpecType) return null;

        if (selectedSpecType === 'table') {
            return (
                <FormField
                    id="specNameTable"
                    label="Spec Name"
                    type="dropdown"
                    value={specName}
                    onChange={(e) => setSpecName(e.target.value)}
                    options={availableTableSpecOptions}
                    required={true}
                    emptyOptionLabel="Select from list"
                />
            );
        } else {
            // text spec
            return (
                <FormField
                    id="specNameText"
                    label="Spec Name"
                    type="voluntary-dropdown"
                    value={specName}
                    onChange={(e) => setSpecName(e.target.value)}
                    options={availableTextSpecOptions}
                    required={true} // The field itself isn't required, but it is for the button if empty
                    emptyOptionLabel="Select or type spec name"
                    placeholder="Select from list or use custom name"
                />
            );
        }
    };

    // Determine if the Next button should be disabled
    const isNextDisabled = () => {
        if (!selectedSpecType) return true;
        if (selectedSpecType === 'table') {
            return !specName || !isTableSpecValueValid(specName);
        }
        // For text spec, allow empty specName if the business logic permits.
        // If specName is truly optional for text, this condition might change.
        // Currently, if specName is empty, it will be disabled.
        return !specName; // If specName cannot be empty for text spec either.
        // If text specName can be empty: return false;
    };

    return (
        <ModalBase onClose={onClose} width="500px">
            <div className="add-spec-modal-content">
                <h2>Add New Spec</h2>

                <ExplanationField>
                    <p>
                        A <strong>spec</strong> is an element of the pack copy
                        that will be checked on the label.
                    </p>
                    <p>
                        Select <strong>Text Spec</strong> for normal text
                        elements like Ingredients, Instructions for Use, etc.
                    </p>
                    <p>
                        Select <strong>Table Spec</strong> for elements like a
                        Nutritional Table.
                    </p>
                </ExplanationField>

                <GenericSelector
                    options={currentSpecTypeOptions}
                    selectedValue={selectedSpecType}
                    onSelectValue={(specType) => {
                        setSelectedSpecType(specType);
                        setSpecName('');
                    }}
                    className="spec-type-selector"
                />

                {renderSpecNameField()}

                <div className="button-row">
                    <button className="btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleNext}
                        disabled={isNextDisabled()}
                    >
                        Next
                    </button>
                </div>
            </div>
        </ModalBase>
    );
};

export default AddSpecModal;
